import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, RefreshCw, Settings, Power, Activity, AlertCircle, AlertTriangle, CheckCircle, Server, Eye, Trash2, Router, Cpu, ServerCog } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import EdgeModal from '../components/edges/EdgeModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import PendingEdgesTable from '../components/edges/PendingEdgesTable';
import StatCard from '../components/common/StatCard';
import { useToast } from '../components/common/ToastContainer';
import type { DeviceStatus } from '../types';
import { edgeService, observabilityService, tenantService } from '../services';
import systemService from '../services/system.service';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES } from '../utils/constants';
import type { Edge as ApiEdge } from '../services';

/**
 * The API's online verdict per edge, keyed by lower-cased serial (GUID).
 *
 * The badge and the Online/Offline counts on this page use the API's
 * `isOnline` from GET /api/v1/edges — the rule the dashboard count and the edge
 * detail use too: a connected sample within 120 s (VSNP-1470). They used to
 * treat "has a CPU sample in /observability/edges/telemetry" as online, which
 * VictoriaMetrics answers for its ~5 min lookback, so an edge silent for 2–5
 * minutes read Online here and offline on the dashboard (VSNP-841). Telemetry
 * still feeds the CPU / memory / uptime columns.
 */
type OnlineBySerial = Record<string, boolean>;

const onlineBySerialFrom = (items: ApiEdge[]): OnlineBySerial =>
  Object.fromEntries(
    items
      .filter((e) => !!e.devSerNum)
      .map((e) => [e.devSerNum.toLowerCase(), e.isOnline === true]),
  );

const isEdgeOnline = (onlineBySerial: OnlineBySerial, serialNumber: string): boolean =>
  onlineBySerial[serialNumber.toLowerCase()] === true;

interface Edge {
  id: string;
  name: string;
  type?: string; // Edge type (Cloud Edge, NaaS Edge, Edge) - optional for EdgeModal compatibility
  location: string;
  model: string;
  serialNumber: string;
  status: DeviceStatus;
  ipAddress: string;
  version: string;
  vppMode: string;
  uptime: string;
  cpu: string;
  memory: string;
  lastSeen: string;
  guid?: string; // For certificate provisioning workflow
  certStatus?: 'pending_approval' | 'approved' | 'provisioned' | 'revoked'; // Certificate status
  devType?: string | number; // Edge type for icon selection
  createTime?: string; // Provisioned/registered date
}

const Edges = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { user } = useAuthStore();
  const { selectedTenantId: topbarTenantId } = useTenantStore();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEdge, setEditingEdge] = useState<Edge | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; edge?: Edge }>({ show: false });
  const [deleting, setDeleting] = useState(false);
  const [rebootConfirm, setRebootConfirm] = useState<{ show: boolean; edge?: Edge }>({ show: false });
  const [rebooting, setRebooting] = useState(false);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [telemetry, setTelemetry] = useState<Record<string, any>>({});
  const [onlineBySerial, setOnlineBySerial] = useState<OnlineBySerial>({});
  const [configStates, setConfigStates] = useState<Record<string, any>>({});
  const [pendingCount, setPendingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page] = useState(1);
  const [pageSize] = useState(100); // Fetch more edges to show all devices
  const [gvsaConfigured, setGvsaConfigured] = useState(false);
  const [checkingGvsa, setCheckingGvsa] = useState(true);

  // Helper to get edge type icon based on Type field
  const getEdgeTypeIcon = (edge: Edge) => {
    const typeStr = String(edge.type || '').toLowerCase();
    if (typeStr.includes('cloud')) {
      return Cpu; // Chip/Compute icon for Cloud Edge
    }
    if (typeStr.includes('naas')) {
      return ServerCog; // Rack-mounted/stacked server icon for NaaS Edge
    }
    return Router; // Default Router icon for Edge type
  };

  // Map API edge to UI edge format
  const mapApiEdgeToUi = (apiEdge: ApiEdge): Edge => {
    // dev_type stores type label ("Edge", "Cloud Edge", "NaaS Edge")
    // Also accept legacy numeric values (1=Edge, 2=Cloud Edge, 3=NaaS Edge)
    let typeString = '';
    const devType = apiEdge.devType;
    if (typeof devType === 'string' && devType.length > 1) {
      // Already a label string
      typeString = devType;
    } else if (devType !== undefined) {
      const numType = Number(devType);
      if (numType === 2) typeString = 'Cloud Edge';
      else if (numType === 3) typeString = 'NaaS Edge';
      else typeString = 'Edge';
    }
    if (!typeString) typeString = 'Edge';

    return {
      id: apiEdge.id,
      name: apiEdge.devName,
      type: typeString,
      location: apiEdge.devLocation || '',
      model: (apiEdge as any).edgeModel || apiEdge.devModel || '-',
      serialNumber: apiEdge.devSerNum,
      // Overwritten from onlineBySerial on every render (see tableEdges).
      status: apiEdge.isOnline === true ? 'Online' : 'Offline',
      ipAddress: apiEdge.publicIp || apiEdge.ip || '-',
      version: apiEdge.agentVersion || apiEdge.firmwareVersion || apiEdge.devVersion || '-',
      vppMode: apiEdge.vppMode || '-',
      uptime: '-',
      cpu: '-',
      memory: '-',
      lastSeen: apiEdge.updateTime || '-',
      guid: apiEdge.devSerNum,
      certStatus: (apiEdge as any).status || undefined,
      devType: apiEdge.devType,
      createTime: apiEdge.createTime,
    };
  };

  // Fetch edges from API
  const fetchEdges = async () => {
    try {
      setLoading(true);
      const response = await edgeService.list({ page, pageSize });
      const uiEdges = response.items.map(mapApiEdgeToUi);
      setEdges(uiEdges);
      setOnlineBySerial(onlineBySerialFrom(response.items));
      // Store total count from API response
      if (response.pagination) {
        setTotalCount(response.pagination.total);
      }
      // Fetch telemetry immediately after edges
      fetchTelemetry(false);
    } catch (error) {
      console.error('Failed to fetch edges:', error);
      // Keep existing edges on error
    } finally {
      setLoading(false);
    }
  };

  // Background refresh of the API's online verdicts only. `edges` is left
  // alone: replacing it would restart the config-state polling below.
  const refreshOnline = async () => {
    try {
      const response = await edgeService.list({ page, pageSize }, { showLoading: false });
      setOnlineBySerial(onlineBySerialFrom(response.items));
    } catch (error) {
      console.error('Failed to refresh edge online status:', error);
    }
  };

  const fetchTelemetry = async (isBackground = false) => {
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      const res = await observabilityService.getEdgesTelemetry(options);
      if (res.status === 'success') {
        setTelemetry(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch telemetry:', error);
    }
  };

  // Fetch V2 config states for all edges
  const fetchConfigStates = async (edgeIds: string[], isBackground = false) => {
    if (edgeIds.length === 0) return;
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      const response = await edgeService.getConfigStates(edgeIds, options);
      // Backend returns { status, data: { edgeId: state } } — use data directly as map
      const statesMap = response?.data || response || {};
      setConfigStates(statesMap);
    } catch (error) {
      console.error('Failed to fetch config states:', error);
    }
  };

  useEffect(() => {
    fetchEdges();
    loadPendingCount();
    checkGvsaStatus();

    // Refresh telemetry and the online verdicts every 30 seconds
    const interval = setInterval(() => {
      fetchTelemetry(true);
      refreshOnline();
    }, 30000);
    return () => clearInterval(interval);
  }, [page, pageSize]);

  // Fetch config states when edges change
  useEffect(() => {
    if (edges.length > 0) {
      const edgeIds = edges.map(e => e.serialNumber);
      fetchConfigStates(edgeIds);

      // Refresh config states every 10 seconds
      const interval = setInterval(() => fetchConfigStates(edgeIds, true), 10000);
      return () => clearInterval(interval);
    }
  }, [edges]);

  const checkGvsaStatus = async () => {
    try {
      setCheckingGvsa(true);
      if (isSuperAdmin) {
        // SuperAdmin: check if Global VSA credentials are configured system-wide
        const response = await systemService.getGlobalVsaCredentials();
        setGvsaConfigured(!!response.configured);
      } else {
        // TenantAdmin: their own tenant. ChannelAdmin: the tenant selected in
        // the topbar — they have no tenant of their own, so user.tenantId is
        // null and reading it left Add Edge permanently disabled. Either way the
        // button unlocks when THAT tenant is Global VSA-registered (registering
        // it stays a SuperAdmin action, matching the "contact your administrator"
        // hint). getGlobalVsaStatus is scope-checked server-side, so a
        // ChannelAdmin only ever reads a tenant inside their own channels.
        const tenantId = user?.tenantId || topbarTenantId;
        if (tenantId) {
          const status = await tenantService.getGlobalVsaStatus(tenantId);
          setGvsaConfigured(!!status?.registered);
        } else {
          setGvsaConfigured(false);
        }
      }
    } catch (error) {
      console.error('Failed to check GVSA status:', error);
      setGvsaConfigured(false);
    } finally {
      setCheckingGvsa(false);
    }
  };

  const loadPendingCount = async () => {
    try {
      const response = await edgeService.listPendingEdges();
      // Safety check: ensure response exists
      if (response && typeof response.count === 'number') {
        setPendingCount(response.count);
      } else {
        console.warn('Invalid response from pending edges:', response);
        setPendingCount(0);
      }
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  const columns: Column<Edge>[] = [
    {
      key: 'name',
      label: 'Edge Name',
      sortable: true,
      render: (edge) => {
        const IconComponent = getEdgeTypeIcon(edge);
        return (
          <button
            onClick={() => navigate(`/edges/${edge.serialNumber}`)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <IconComponent className="w-4 h-4 text-primary-600" />
            <span className="font-medium">{edge.name}</span>
          </button>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
    },
    {
      key: 'createTime',
      label: 'Provisioned',
      sortable: true,
      render: (edge) => {
        if (!edge.createTime) return '-';
        return <span>{formatDate(edge.createTime, useSettingsStore.getState().getDateFormatSettings())}</span>;
      },
    },
    {
      key: 'model',
      label: 'Model',
      sortable: true,
    },
    {
      key: 'serialNumber',
      label: 'Serial Number',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (edge) => {
        const online = isEdgeOnline(onlineBySerial, edge.serialNumber);
        return <StatusBadge status={online ? 'Online' : 'Offline'} />;
      },
    },
    {
      key: 'ipAddress',
      label: 'IP Address',
    },
    {
      key: 'vppMode',
      label: 'Mode',
      sortable: true,
      render: (edge) => {
        const mode = edge.vppMode?.toUpperCase() || '';
        if (mode.includes('DPDK')) {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              DPDK
            </span>
          );
        }
        if (mode.includes('AF_PACKET') || mode.includes('AFPACKET')) {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
              AF_PACKET
            </span>
          );
        }
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            {edge.vppMode || 'Unknown'}
          </span>
        );
      },
    },
    {
      key: 'version',
      label: 'Version',
      render: (edge) => <span className="font-mono text-xs">{edge.version}</span>,
    },
    {
      key: 'uptime',
      label: 'Uptime',
      render: (edge) => {
        const stats = telemetry[edge.serialNumber.toLowerCase()];
        if (!stats || stats.uptime === undefined) return '-';
        const seconds = stats.uptime;
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return <span>{days}d {hours}h {minutes}m</span>;
      }
    },
    {
      key: 'cpu',
      label: 'CPU',
      sortable: true,
      render: (edge) => {
        const stats = telemetry[edge.serialNumber.toLowerCase()];
        if (!stats || stats.cpu === undefined) return '-';
        const val = stats.cpu;
        return (
          <div className="flex items-center space-x-2">
            <div className="w-12 bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${val > 80 ? 'bg-red-500' : val > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(val, 100)}%` }}
              ></div>
            </div>
            <span className="text-xs font-medium">{val.toFixed(1)}%</span>
          </div>
        );
      }
    },
    {
      key: 'memory',
      label: 'Memory',
      sortable: true,
      render: (edge) => {
        const stats = telemetry[edge.serialNumber.toLowerCase()];
        if (!stats || stats.memory === undefined) return '-';
        const val = stats.memory;
        return (
          <div className="flex items-center space-x-2">
            <div className="w-12 bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${val > 80 ? 'bg-red-500' : val > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(val, 100)}%` }}
              ></div>
            </div>
            <span className="text-xs font-medium">{val.toFixed(1)}%</span>
          </div>
        );
      }
    },
    {
      key: 'config',
      label: 'Config',
      render: (edge) => {
        const state = configStates[edge.serialNumber];
        const stats = telemetry[edge.serialNumber.toLowerCase()];
        const driftCount = stats?.drift_count || 0;

        const driftBadge = driftCount > 0 ? (
          <span className="inline-flex items-center space-x-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 ml-1" title={`${driftCount} config drift${driftCount > 1 ? 's' : ''} detected`}>
            <AlertTriangle className="w-3 h-3" />
            <span>{driftCount}</span>
          </span>
        ) : null;

        // If no V2 state available, show unknown
        if (!state) {
          return (
            <div className="flex items-center">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                Unknown
              </span>
              {driftBadge}
            </div>
          );
        }

        // Failed status - red
        if (state.syncStatus === 'failed') {
          return (
            <div className="flex items-center">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700" title={state.lastError || 'Sync failed'}>
                Failed
              </span>
              {driftBadge}
            </div>
          );
        }

        // Dirty/pending changes - yellow
        if (state.isDirty) {
          return (
            <div className="flex items-center">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700" title={state.dirtyReason || 'Changes pending'}>
                Pending
              </span>
              {driftBadge}
            </div>
          );
        }

        // Applying - blue
        if (state.syncStatus === 'applying') {
          return (
            <div className="flex items-center">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                Applying...
              </span>
              {driftBadge}
            </div>
          );
        }

        // Synced - green
        if (state.syncStatus === 'synced') {
          return (
            <div className="flex items-center">
              <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
                <span>✓</span>
                <span>Synced</span>
              </span>
              {driftBadge}
            </div>
          );
        }

        // Default unknown state
        return (
          <div className="flex items-center">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
              {state.syncStatus || 'Unknown'}
            </span>
            {driftBadge}
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (edge) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => navigate(`/edges/${edge.serialNumber}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canUpdate && (
            <button
              onClick={() => setEditingEdge(edge)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Configure"
            >
              <Settings className="w-4 h-4 text-gray-600 hover:text-primary-600" />
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => setRebootConfirm({ show: true, edge })}
              className="p-1.5 hover:bg-yellow-50 rounded transition-colors"
              title="Reboot Edge"
            >
              <Power className="w-4 h-4 text-gray-600 hover:text-warning-600" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, edge })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete Edge"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleSaveEdge = async (edge: any, tenantId?: string) => {
    try {
      if (editingEdge) {
        // Update existing edge
        await edgeService.update(edge.id, {
          devName: edge.name,
          devType: edge.type || 'Edge',
          devLocation: edge.location || '',
        });
        toast.success('Edge Updated', 'Edge information has been updated successfully');
      } else {
        // Create new edge
        await edgeService.create({
          devSerNum: edge.serialNumber,
          devName: edge.name,
          devLocation: edge.location,
          devModel: edge.model,
          tenantId, // Pass the selected tenant ID
        });
        toast.success('Edge Created', 'Edge has been created successfully');
      }
      // Refresh the list
      await fetchEdges();
      setIsAddModalOpen(false);
      setEditingEdge(undefined);
    } catch (error: any) {
      console.error('Failed to save edge:', error);
      toast.error('Operation Failed', error.message || 'Failed to save edge. Please try again.');
    }
  };

  const handleDeleteEdge = async () => {
    if (!deleteConfirm.edge) return;

    try {
      setDeleting(true);
      await edgeService.delete(deleteConfirm.edge.id);
      toast.success('Edge Deleted', `Edge "${deleteConfirm.edge.name}" has been deleted successfully`);
      setDeleteConfirm({ show: false });
      await fetchEdges();
    } catch (error: any) {
      console.error('Failed to delete edge:', error);
      toast.error('Delete Failed', error.message || 'Failed to delete edge. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRebootEdge = async () => {
    if (!rebootConfirm.edge) return;

    try {
      setRebooting(true);
      await edgeService.reboot(rebootConfirm.edge.serialNumber);
      toast.success('Edge Rebooting', `Edge "${rebootConfirm.edge.name}" reboot has been initiated`);
      setRebootConfirm({ show: false });
    } catch (error: any) {
      console.error('Failed to reboot edge:', error);
      toast.error('Reboot Failed', error.message || 'Failed to reboot edge. Please try again.');
    } finally {
      setRebooting(false);
    }
  };

  const handleRefresh = () => {
    fetchEdges();
  };

  const onlineCount = edges.filter((e) => isEdgeOnline(onlineBySerial, e.serialNumber)).length;
  // The table sorts on `status`: keep it the badge's verdict, refreshed with it.
  const tableEdges = edges.map((e) => ({
    ...e,
    status: (isEdgeOnline(onlineBySerial, e.serialNumber) ? 'Online' : 'Offline') as DeviceStatus,
  }));
  const offlineCount = edges.length - onlineCount;
  const issuesCount = edges.filter((e) => {
    const stats = telemetry[e.serialNumber.toLowerCase()];
    if (!stats) return false;
    return (stats.cpu > 80 || (stats.memory && stats.memory > 80));
  }).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edge Devices</h1>
          <p className="text-gray-600 mt-1">Manage your network edge gateways and routers</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="btn-secondary inline-flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
          <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              disabled={!gvsaConfigured || checkingGvsa}
              className={`btn-primary inline-flex items-center space-x-2 ${(!gvsaConfigured || checkingGvsa) ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!gvsaConfigured ? (isSuperAdmin ? 'Global VSA credentials not configured. Please configure in System Settings.' : 'Your tenant is not registered with Global VSA. Contact your administrator.') : 'Add New Edge'}
            >
              <Plus className="w-4 h-4" />
              <span>Add Edge</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'active'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Active Edges
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${activeTab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <span>Pending Approval</span>
              {pendingCount > 0 && (
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none rounded-full ${activeTab === 'pending'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-orange-100 text-orange-700 animate-pulse'
                  }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Metrics - Only show for active tab */}
      {activeTab === 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            icon={Server}
            title="Total Edges"
            value={totalCount}
            color="primary"
          />
          <StatCard
            icon={CheckCircle}
            title="Online"
            value={onlineCount}
            color="success"
          />
          <StatCard
            icon={Activity}
            title="Offline"
            value={offlineCount}
            color="secondary"
          />
          <StatCard
            icon={AlertCircle}
            title="Issues"
            value={issuesCount}
            color="warning"
          />
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'active' ? (
        <div className="card">
          <DataTable
            columns={columns}
            data={tableEdges}
            searchPlaceholder="Search edges by name, location, or IP..."
            loading={loading}
          />
        </div>
      ) : (
        <div className="card">
          <PendingEdgesTable />
        </div>
      )}

      {/* Modals */}
      <EdgeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveEdge}
        mode="add"
        // A TenantAdmin's own tenant, else the topbar selection. SuperAdmin and
        // ChannelAdmin both have null user.tenantId and operate on the selected
        // tenant; the previous `!isSuperAdmin ? user.tenantId : topbar` split
        // sent a ChannelAdmin's modal an undefined tenant.
        fixedTenantId={user?.tenantId || topbarTenantId || undefined}
      />
      <EdgeModal
        isOpen={!!editingEdge}
        onClose={() => setEditingEdge(undefined)}
        onSave={handleSaveEdge}
        edge={editingEdge}
        mode="edit"
      />
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Edge"
        message={`Are you sure you want to delete "${deleteConfirm.edge?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteEdge}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />
      <ConfirmDialog
        isOpen={rebootConfirm.show}
        title="Reboot Edge"
        message={`Are you sure you want to reboot "${rebootConfirm.edge?.name}"? This will cause a service interruption.`}
        confirmText="Reboot"
        variant="danger"
        onConfirm={handleRebootEdge}
        onClose={() => setRebootConfirm({ show: false })}
        isLoading={rebooting}
      />
    </div>
  );
};

export default Edges;

